import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Switch } from 'ui/switch';
import { Loader } from 'ui/loader';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import Button from 'components/partials/button/button';
import { getInsightUsersTableSettings } from 'components/partials/insight-modal/components/user-access-step/user-access-step.config';
import { useInsightAccess } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/hooks/use-insight-access';

import type { InsightDto } from 'api/generated/schemas/insightDto';

interface AccessTabProps {
  insight: InsightDto | null | undefined;
  clientId: string | number | undefined;
}

const AccessTab = ({
  insight,
  clientId,
}: AccessTabProps): React.JSX.Element => {
  const {
    loading,
    saving,
    displayedData,
    isAllUsersSelected,
    isIndeterminate,
    isToggleOn,
    hasChanges,
    onSelectAllUsers,
    onUserChange,
    onToggleChange,
    handleSave,
    handleCancel,
  } = useInsightAccess({ insight, clientId });

  const columns = getInsightUsersTableSettings({
    onUserChange,
    onSelectAllUsers,
    isAllUsersSelected,
    isIndeterminate,
  });

  if (!displayedData.length && !loading) {
    return (
      <EmptySpaceContent
        customClass="flex-1"
        text="No users have been assigned yet"
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Loader loading={loading || saving} backdrop="white" fullScreen={false} />
      <TableComponent
        columns={columns}
        data={displayedData}
        customHeight="max-h-[500px]"
        pagination={false}
        showingRecordsTextIsOn={false}
        viewByAmountSelectIsOn={false}
        isLoading={loading || saving}
      />

      <Stack
        sx={{ alignItems: 'flex-start', justifyContent: 'flex-start', mt: 2 }}
      >
        <Switch
          label="Assign to all new users"
          checked={isToggleOn}
          onChange={(_event, checked) => onToggleChange(checked)}
          data-testid="access-tab-toggle"
        />

        {hasChanges && (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 1.5, mt: 2.5 }}
          >
            <Button
              variant="brand-filled"
              size="sm"
              onClick={handleSave}
              loading={saving}
              data-testid="update-access-tab-button"
            >
              Update access
            </Button>
            <Button
              variant="reserve-outlined"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
              data-testid="cancel-access-tab-button"
            >
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default AccessTab;
